import { getApiBaseUrl } from '../config/apiConfig';

// Configuration de l'API Symfony
const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  timeout: 30000 // 30 secondes
};

class ApiService {
  constructor() {
    this.isConfiguredFlag = !!(API_CONFIG.baseUrl);
  }

  // Vérifier si le service est configuré
  isConfigured() {
    return this.isConfiguredFlag;
  }

  // Effectuer une requête à l'API Symfony
  async makeRequest(method, endpoint, body = null, params = {}, options = {}) {
    if (!this.isConfigured()) {
      throw new Error('Service API non configuré. Veuillez configurer l\'URL de l\'API.');
    }

    const url = new URL(`${API_CONFIG.baseUrl}${endpoint}`);
    
    // Ajouter les paramètres de requête
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    const isForm = options.formData === true;
    const headers = {
      Accept: options.blob ? '*/*' : 'application/json',
    };
    if (!isForm) {
      headers['Content-Type'] = 'application/json';
    }

    // Ajouter le token JWT si disponible
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions = {
      method,
      headers,
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      requestOptions.body = isForm ? body : JSON.stringify(body);
    }

    const timeoutMs = options.timeout ?? API_CONFIG.timeout;
    let timeoutId = null;

    try {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const externalSignal = options.signal;
      const onExternalAbort = () => controller.abort();
      if (externalSignal) {
        if (externalSignal.aborted) {
          clearTimeout(timeoutId);
          throw new DOMException('Aborted', 'AbortError');
        }
        externalSignal.addEventListener('abort', onExternalAbort);
      }

      const response = await fetch(url.toString(), {
        ...requestOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      timeoutId = null;
      if (externalSignal) {
        externalSignal.removeEventListener('abort', onExternalAbort);
      }

      if (!response.ok) {
        const raw = await response.text();
        let errorData = {};
        try {
          errorData = raw ? JSON.parse(raw) : {};
        } catch {
          errorData = {};
        }
        
        // Si erreur 401 (non autorisé), nettoyer le token et laisser le composant gérer la redirection
        if (response.status === 401) {
          // Nettoyer le token invalide
          localStorage.removeItem('auth_token');
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('user_data');
          // Ne pas rediriger ici, laisser le composant ProtectedRoute gérer
        }
        
        const plain = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 180);
        const detail = errorData.error || errorData.detail || errorData.message || response.statusText || plain;
        throw new Error(`Erreur API ${response.status} sur ${endpoint}: ${detail}`);
      }

      if (options.blob) {
        return await response.blob();
      }

      return await response.json();
    } catch (error) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (error.name === 'AbortError') {
        throw new Error(
          `Délai dépassé (${Math.round(timeoutMs / 1000)} s). L'opération a été interrompue, réessayez.`
        );
      }
      const raw = String(error.message || '');
      if (
        raw === 'Failed to fetch'
        || raw === 'Load failed'
        || raw.includes('NetworkError')
        || raw.includes('Failed to fetch')
      ) {
        throw new Error('Le serveur ne répond pas. Attendez deux secondes puis réessayez.');
      }
      console.error(`Erreur lors de la requête ${method} ${endpoint}:`, error);
      throw error;
    }
  }

  // Vérifier la santé de l'API
  async health() {
    try {
      const result = await this.makeRequest('GET', '/health');
      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Récupérer la liste des domaines avec pagination et filtres
  async getDomainsWithPagination(options = {}) {
    const {
      page = 1,
      limit = 20,
      search = '',
      status = null,
      sort = 'name',
      order = 'ASC'
    } = options;

    try {
      const result = await this.makeRequest('GET', '/domains/ovh', null, {
        page,
        limit,
        search,
        status,
        sort,
        order
      });

      return {
        domains: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des domaines:', error);
      throw error;
    }
  }

  // Récupérer un domaine spécifique par ID
  async getDomainById(id) {
    try {
      const result = await this.makeRequest('GET', `/domains/${id}`);
      return result.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du domaine ${id}:`, error);
      throw error;
    }
  }

  // Récupérer un domaine par nom
  async getDomainByName(name) {
    try {
      const result = await this.makeRequest('GET', '/domains', null, {
        search: name,
        limit: 1
      });
      
      if (result.domains && result.domains.length > 0) {
        return result.domains[0];
      }
      return null;
    } catch (error) {
      console.error(`Erreur lors de la récupération du domaine ${name}:`, error);
      throw error;
    }
  }

  // Synchroniser les domaines avec l'API OVH
  async syncDomains() {
    try {
      const result = await this.makeRequest('POST', '/domains/sync', null, {}, { timeout: 600000 });
      return result;
    } catch (error) {
      console.error('Erreur lors de la synchronisation des domaines:', error);
      throw error;
    }
  }

  // Récupérer les statistiques des domaines
  async getDomainStats() {
    try {
      const result = await this.makeRequest('GET', '/domains/stats/overview');
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }

  // Méthode pour tester la connexion API
  async testConnection() {
    try {
      const result = await this.health();
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Méthodes génériques pour les requêtes HTTP
  async get(endpoint, params = {}, options = {}) {
    return await this.makeRequest('GET', endpoint, null, params, options);
  }

  async post(endpoint, body = null, params = {}, options = {}) {
    return await this.makeRequest('POST', endpoint, body, params, options);
  }

  async put(endpoint, body = null, params = {}) {
    return await this.makeRequest('PUT', endpoint, body, params);
  }

  async patch(endpoint, body = null, params = {}, options = {}) {
    return await this.makeRequest('PATCH', endpoint, body, params, options);
  }

  async delete(endpoint, params = {}) {
    return await this.makeRequest('DELETE', endpoint, null, params);
  }

  // Méthodes de compatibilité avec l'ancien service OVH
  async getDomains() {
    const result = await this.getDomainsWithPagination({ limit: 1000 });
    return result.domains.map(domain => domain.name);
  }

  async getDomainFullInfo(domainName) {
    try {
      const domain = await this.getDomainByName(domainName);
      if (!domain) {
        throw new Error(`Domaine ${domainName} non trouvé`);
      }

      return {
        details: domain.details,
        contacts: domain.contacts,
        dns: domain.dns,
        renewal: domain.renewal,
        errors: {
          details: null,
          contacts: null,
          dns: null,
          renewal: null
        }
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération des informations du domaine ${domainName}:`, error);
      throw error;
    }
  }
}

// Créer une instance singleton du service
const apiService = new ApiService();

export default apiService;